import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { CircularProgress, VeTypography } from "../../../mui";
import WarningSvg from "./WarningSvg";
import { SuccessModal } from "../../Modal/successModal";
import { SubSection } from "./SubSection";

const VolumneForm = ({
  RichText,
  assets,
  DynamicImage,
  getFileTypeImage,
  useQueryData,
  useMutation,
  mutateAction,
  toast,
  useSession,
  submitCurrentStep,
  setSubmitCurrentStep,
  discardStep,
  setDiscardStep,
  projectId,
  setDisableBtn,
  setIsLoading,
  nextClicked,
  setNextClicked,
  prevClicked,
  setPrevClicked,
  setSelectedTab,
  timeZoneDateTimeUser,
  useQueryClient,
  projectDetails,
  setProjectDetails,
  checkSaveState,
  variables,
  setCheckSaveState,
  submitVariables,
  financeFormLoading,
}: any) => {
  const [questionareData, setQuestionareData] = useState<any>();
  const [updateAnswer, setUpdatedAnswers] = useState<any[]>([]);
  const { data: session, update } = useSession();
  const [fetchedQuestionare, setFetchedQuestionare] = useState();
  const [questionMetadata, setQuestionMetadata] = useState([]);
  const [openDiscardModal, setOpenDiscardModal] = useState(false);
  const [saveChangesModal, setSaveChangesModal] = useState(false);

  const queryClient = useQueryClient();
  const data: any = queryClient.getQueryData([
    "questionare",
    "FINANCING",
    projectId,
  ]);
  const metaData: any = queryClient.getQueryData([
    "questionareMetaData",
    "FINANCING",
    projectId,
  ]);

  const handleCloseDiscardModal = () => setOpenDiscardModal(false);
  const handleCloseChangesModal = () => setSaveChangesModal(false);

  const { mutate: updateProject } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-project"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      setProjectDetails(data?.updated_project_data);
      if (!submitVariables?.submitClicked) variables.projectRefetch();
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-answer"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      setFetchedQuestionare(questionareData);
      queryClient.setQueryData(["questionare", "FINANCING", projectId], {
        data: questionareData,
      });

      if (!submitVariables?.submitClicked)
        toast("Application form saved successfully", { type: "success" });

      const stepCompleted = !questionareData.some(
        (obj: any) => obj.requiredQuestionAnswered === false
      );

      if (stepCompleted) {
        updateProject({
          type: "patch",
          endPoint: `/project/v2/onboarding/${projectId}`,
          data: {
            stepper: {
              ...projectDetails?.stepper,
              volumePricing: "COMPLETED",
            },
          },
          session,
          update,
        });
      } else if (projectDetails?.stepper?.volumePricing === "COMPLETED")
        updateProject({
          type: "patch",
          endPoint: `/project/v2/onboarding/${projectId}`,
          data: {
            stepper: {
              ...projectDetails?.stepper,
              volumePricing: "NOT_STARTED",
            },
          },
          session,
          update,
        });

      if (saveChangesModal) {
        handleCloseChangesModal();
        handleDiscardChangesModal();
      }

      if (submitVariables?.submitClicked) {
        if (stepCompleted)
          submitMutate({
            type: "post",
            endPoint: `/project/v2/onboarding/${projectId}/submit`,
            data: {},
            session,
            update,
          });
        else
          toast(
            "Please complete all required fields before submitting the form.",
            { type: "error" }
          );
        submitVariables?.setIsSubmitLoading(false);
        submitVariables?.setSubmitClicked(false);
      }
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const mutateBtn = () =>
    mutate({
      type: "patch",
      endPoint: `/project/v2/onboarding/form/answer?projectId=${projectId}`,
      data: {
        updateAnswer,
      },
      session,
      update,
    });

  useEffect(() => {
    if (submitCurrentStep?.step4) {
      mutateBtn();
      setSubmitCurrentStep({
        ...submitCurrentStep,
        step4: false,
      });
    }
    if (discardStep?.step4) {
      setOpenDiscardModal(true);
    }
    if (nextClicked?.step4) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      ) {
        setSelectedTab(4);
        setNextClicked({
          ...nextClicked,
          step4: false,
        });
      } else setSaveChangesModal(true);
    }
    if (prevClicked?.step4) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      ) {
        setSelectedTab(2);
        setPrevClicked({
          ...prevClicked,
          step4: false,
        });
      } else setSaveChangesModal(true);
    }
  }, [submitCurrentStep, discardStep, nextClicked, prevClicked]);

  const editData = (
    subsection: string,
    questionId: string,
    newAnswer: string
  ) => {
    setQuestionareData((prevData: any) =>
      prevData.map((sec: any) => {
        let updatedAnsweredQuestions = sec.answeredQuestions;

        const updatedSubsections = sec.subsections.map((subsec: any) => {
          if (subsec.subsection === subsection) {
            const updatedQuestions = subsec.questions.map((question: any) => {
              if (question.id === questionId) {
                // Increment `answeredQuestions` if the new answer is not null and the previous answer was null.
                if (!question.answer && newAnswer) {
                  updatedAnsweredQuestions++;
                }
                // Decrement `answeredQuestions` if the new answer is null and the previous answer was not null.
                if (question.answer && !newAnswer) {
                  updatedAnsweredQuestions--;
                }

                // Compare with the original data and update the `updatedAnswers` state.
                const originalQuestion = data?.data
                  ?.flatMap((sec: any) =>
                    sec.subsections.flatMap((subsec: any) => subsec.questions)
                  )
                  ?.find((q: any) => q.id === questionId);

                if (originalQuestion && originalQuestion.answer !== newAnswer) {
                  setUpdatedAnswers((prev: any) => {
                    const existingEntryIndex = prev.findIndex(
                      (item: any) => item.id === questionId
                    );

                    if (existingEntryIndex > -1) {
                      // Update existing entry
                      const updatedArray = [...prev];
                      updatedArray[existingEntryIndex] = {
                        id: questionId,
                        answer: newAnswer,
                      };
                      return updatedArray;
                    } else {
                      // Add new entry
                      return [...prev, { id: questionId, answer: newAnswer }];
                    }
                  });
                }

                return { ...question, answer: newAnswer };
              }
              return question;
            });

            return { ...subsec, questions: updatedQuestions };
          }
          return subsec;
        });

        // Check if all required questions in the section are answered.
        const allQuestions = updatedSubsections.flatMap(
          (subsec: any) => subsec.questions
        );
        const requiredQuestions = allQuestions.filter(
          (question: any) => question.isRequired
        );

        const allRequiredAnswered =
          requiredQuestions.length === 0 ||
          requiredQuestions.every((question: any) => question.answer);

        return {
          ...sec,
          subsections: updatedSubsections,
          answeredQuestions: updatedAnsweredQuestions,
          requiredQuestionAnswered: allRequiredAnswered,
        };
      })
    );
  };

  const updateQuestionMetadata = (
    id: string,
    updates: { docCount?: number; referenceLinks?: string[] }
  ) => {
    setQuestionMetadata((prevMetadata: any) => {
      const updatedValue = prevMetadata.map((item: any) =>
        item.id === id
          ? {
              ...item,
              docCount: updates.docCount ?? item.docCount,
              referenceLinks: updates.referenceLinks ?? item.referenceLinks,
            }
          : item
      );
      queryClient.setQueryData(
        ["questionareMetaData", "FINANCING", projectId],
        updatedValue
      );
      return updatedValue;
    });
  };

  useEffect(() => {
    if (data) {
      let questionIndex = 1; // Initialize the question index counter.

      const newData = data?.data?.map((sec: any) => {
        let updatedAnsweredQuestions = sec.answeredQuestions;
        let allRequiredAnswered = true;

        const allQuestions = sec.subsections.flatMap((subsec: any) =>
          subsec.questions.map((question: any) => ({
            ...question,
            index: questionIndex++, // Assign the index to each question.
            answer: question?.answer || "",
          }))
        );

        const requiredQuestions = allQuestions.filter(
          (question: any) => question.isRequired
        );

        if (requiredQuestions.length === 0) {
          allRequiredAnswered = true;
        } else {
          allRequiredAnswered = requiredQuestions.every(
            (question: any) => question.answer
          );
        }

        // Update subsections with indexed questions.
        const updatedSubsections = sec.subsections.map((subsec: any) => ({
          ...subsec,
          questions: subsec.questions.map((question: any) => ({
            ...question,
            index: allQuestions.find((q: any) => q.id === question.id)?.index,
            answer: question?.answer || "",
          })),
        }));

        return {
          ...sec,
          answeredQuestions: updatedAnsweredQuestions,
          requiredQuestionAnswered: allRequiredAnswered,
          subsections: updatedSubsections,
        };
      });

      // Generate the questionMetadata state.
      const metadata = newData.flatMap((sec: any) =>
        sec.subsections.flatMap((subsec: any) =>
          subsec.questions.map((question: any) => ({
            id: question.id,
            docCount: question.docCount || 0,
            referenceLinks: question.referenceLinks || [],
          }))
        )
      );

      setQuestionareData(newData);
      setFetchedQuestionare(newData);
      setQuestionMetadata(metadata); // Initialize question metadata state.
    }
  }, [data]);

  useEffect(() => {
    if (JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData))
      setDisableBtn((prev: any) => ({ ...prev, discard: true, save: true }));
    else
      setDisableBtn((prev: any) => ({ ...prev, discard: false, save: false }));
  }, [fetchedQuestionare, questionareData]);

  useEffect(() => {
    setIsLoading(isPending);
  }, [isPending]);

  const handleDiscardChangesModal = () => {
    setSelectedTab(
      nextClicked?.step4
        ? 4
        : prevClicked?.step4
          ? 2
          : checkSaveState?.destination
    );
    if (nextClicked?.step4)
      setNextClicked({
        ...nextClicked,
        step4: false,
      });
    else if (prevClicked?.step4)
      setPrevClicked({
        ...prevClicked,
        step4: false,
      });
  };

  useEffect(() => {
    if (
      checkSaveState &&
      checkSaveState?.current !== checkSaveState?.destination
    ) {
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      ) {
        setSelectedTab(checkSaveState?.destination);
      } else setSaveChangesModal(true);
      setCheckSaveState(null);
    }
  }, [checkSaveState]);

  useEffect(() => {
    if (metaData) setQuestionMetadata(metaData);
  }, [metaData]);

  const { mutate: submitMutate } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["submit-project"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      submitVariables?.setSubmitModal(true);
      submitVariables?.setSubmitClicked(false);
      submitVariables?.setIsSubmitLoading(false);
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  useEffect(() => {
    if (submitVariables?.submitClicked) {
      submitVariables?.setIsSubmitLoading(true);
      if (
        JSON.stringify(fetchedQuestionare) === JSON.stringify(questionareData)
      )
        submitMutate({
          type: "post",
          endPoint: `/project/v2/onboarding/${projectId}/submit`,
          data: {},
          session,
          update,
        });
      else mutateBtn();
    }
  }, [submitVariables?.submitClicked]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={"24px"}
      marginTop={"16px"}
    >
      <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
        <VeTypography variant="subtitle1Main">
          Project Economics and Deliverables
        </VeTypography>
        <VeTypography variant="body2Tertiary">
          Please answer the questions in each section to provide a clear
          understanding of your project. Ensure that all mandatory questions are
          completed, as they are required to finalize the application form and
          proceed with the next steps.
        </VeTypography>
      </Box>
      {financeFormLoading ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          height={"50vh"}
        >
          <CircularProgress
            sx={{
              color: "var(--vc-violet)",
            }}
          />
        </Box>
      ) : questionareData?.length === 0 ? (
        <Box
          display={"flex"}
          marginBottom={"50px"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          paddingTop={"24px"}
          minHeight={"300px"}
        >
          {DynamicImage(assets?.file, "file", 80, 80)}
          <VeTypography marginTop={"12px"} variant="label2StrongerSecondary">
            No questions to display!
          </VeTypography>
        </Box>
      ) : (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={"16px"}
          paddingBottom={"24px"}
        >
          <Box
            sx={{
              position: "sticky",
              top: "0px",
              zIndex: 500,
              backgroundColor: "white",
            }}
          >
            <VeTypography variant="label2Tertiary">
              Completed:{" "}
              {questionareData?.reduce(
                (acc: number, ans: any) => acc + ans?.answeredQuestions,
                0
              )}
              /
              {questionareData?.reduce(
                (acc: number, ques: any) => acc + ques?.totalQuestions,
                0
              )}
            </VeTypography>
          </Box>
          {questionareData?.map((sectionData: any, index: number) => (
            <Box
              key={sectionData.section}
              // ref={(el) => (sectionRefs.current[sectionData.section] = el)}
              id={`Section-${index + 1}`}
              className="sections"
              data-section={sectionData.section}
            >
              {sectionData?.subsections?.map((item: any, index: number) => (
                <SubSection
                  data={item}
                  RichText={RichText}
                  editData={editData}
                  key={index}
                  assets={assets}
                  DynamicImage={DynamicImage}
                  getFileTypeImage={getFileTypeImage}
                  useSession={useSession}
                  useMutation={useMutation}
                  useQueryData={useQueryData}
                  mutateAction={mutateAction}
                  toast={toast}
                  projectId={projectId}
                  updateQuestionMetadata={updateQuestionMetadata}
                  questionMetadata={questionMetadata}
                  timeZoneDateTimeUser={timeZoneDateTimeUser}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}
      <SuccessModal
        width="436px"
        open={openDiscardModal}
        onClose={handleCloseDiscardModal}
        title="Discard changes?"
        desc="There are unsaved changes in this step that will be lost if you don’t save them."
        containedButtonTitle="Yes"
        outlinedButtonTitle="No"
        outlinedOnClick={handleCloseDiscardModal}
        containedOnClick={() => {
          setQuestionareData(fetchedQuestionare);
          setDiscardStep({
            ...discardStep,
            step4: false,
          });
          handleCloseDiscardModal();
        }}
        iconSvg={<WarningSvg />}
        styleColor="25855A"
      />
      <SuccessModal
        width="436px"
        open={saveChangesModal}
        onClose={handleCloseChangesModal}
        title="Save recent changes?"
        desc="There are unsaved changes in this step that will be lost if you don’t save them."
        containedButtonTitle="Save Changes"
        outlinedButtonTitle="Discard"
        outlinedOnClick={handleDiscardChangesModal}
        loading={isPending}
        containedOnClick={mutateBtn}
        iconSvg={<WarningSvg />}
        styleColor="25855A"
      />
    </Box>
  );
};

export default VolumneForm;
